import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, Plus, Trash2, X, Save, Power, Pencil, Hash, Bookmark, BookmarkPlus, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

const BLANK = {
  channel: "", channel_name: "", enabled: true, use_embed: false,
  content: "📌 Welcome! Read the rules and enjoy your stay in {server}.",
  embed_title: "", embed_color: "#0EA5E9", embed_footer: "", image_url: "", delay_seconds: 5,
};
const VARS = ["{server}", "{count}", "{channel}"];

export default function StickyMessages() {
  const { guildId } = useGuild();
  const [items, setItems] = useState(null);
  const [channels, setChannels] = useState([]);
  const [linked, setLinked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tplName, setTplName] = useState("");

  const load = () => api.get(`/guilds/${guildId}/stickies`).then((r) => setItems(r.data)).catch(() => setItems([]));
  const loadTpl = () => api.get(`/guilds/${guildId}/sticky-templates`).then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadTpl();
    api.get(`/guilds/${guildId}/discord/channels`).then((r) => { setChannels(r.data.channels || []); setLinked(r.data.linked); }).catch(() => {});
  }, [guildId]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(BLANK); setEditingId(null); };

  const edit = (s) => {
    setForm({ channel: s.channel, channel_name: s.channel_name, enabled: s.enabled, use_embed: s.use_embed,
      content: s.content, embed_title: s.embed_title, embed_color: s.embed_color || "#0EA5E9",
      embed_footer: s.embed_footer, image_url: s.image_url, delay_seconds: s.delay_seconds || 5 });
    setEditingId(s.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.channel) return toast.error("Pick a channel");
    if (!form.content.trim() && !form.embed_title.trim()) return toast.error("Add message content or an embed title");
    const ch = channels.find((c) => String(c.id) === String(form.channel));
    const payload = { ...form, channel_name: ch?.name || form.channel_name };
    setBusy(true);
    try {
      if (editingId) await api.put(`/guilds/${guildId}/stickies/${editingId}`, payload);
      else await api.post(`/guilds/${guildId}/stickies`, payload);
      toast.success(editingId ? "Sticky updated — applies within ~1 min" : "Sticky created — applies within ~1 min");
      reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const toggle = async (s) => {
    try { await api.put(`/guilds/${guildId}/stickies/${s.id}`, { ...s, enabled: !s.enabled }); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this sticky message? The pinned message will be removed from the channel.")) return;
    try { await api.delete(`/guilds/${guildId}/stickies/${id}`); toast.success("Deleted"); if (editingId === id) reset(); load(); }
    catch { toast.error("Failed"); }
  };

  const saveTemplate = async () => {
    const name = (tplName || "").trim();
    if (!name) return toast.error("Name your template");
    try {
      await api.post(`/guilds/${guildId}/sticky-templates`, {
        name, use_embed: form.use_embed, content: form.content, embed_title: form.embed_title,
        embed_color: form.embed_color, embed_footer: form.embed_footer, image_url: form.image_url });
      toast.success("Template saved"); setTplName(""); loadTpl();
    } catch { toast.error("Failed to save template"); }
  };
  const applyTemplate = (t) => {
    setForm((f) => ({ ...f, use_embed: t.use_embed, content: t.content, embed_title: t.embed_title,
      embed_color: t.embed_color || "#0EA5E9", embed_footer: t.embed_footer, image_url: t.image_url }));
    toast.success(`Loaded "${t.name}"`);
  };
  const deleteTemplate = async (id) => {
    try { await api.delete(`/guilds/${guildId}/sticky-templates/${id}`); loadTpl(); } catch { toast.error("Failed"); }
  };

  const insertVar = (v) => set("content", (form.content || "") + " " + v);

  return (
    <div className="space-y-6" data-testid="sticky-page">
      <PageHead title="Sticky Messages"
        desc="Keep a message pinned to the bottom of a channel — it auto-reposts as new messages come in. Perfect for rules, links and reminders." />

      {!linked && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-300 text-sm">
          Link your Discord server first (Overview) so channels load and the bot can post.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Editor */}
        <div className="lg:col-span-3 bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-head font-semibold flex items-center gap-2">{editingId ? <Pencil className="w-4 h-4 text-volt" /> : <Plus className="w-4 h-4 text-volt" />} {editingId ? "Edit sticky" : "New sticky"}</h3>
            {editingId && <button data-testid="sticky-cancel" onClick={reset} className="text-xs text-zinc-400 flex items-center gap-1"><X className="w-3 h-3" /> Cancel edit</button>}
          </div>

          <div>
            <label className="text-xs text-zinc-500 flex items-center gap-1.5 mb-1"><Hash className="w-3 h-3" /> Channel</label>
            <select data-testid="sticky-channel" value={form.channel} onChange={(e) => set("channel", e.target.value)}
              className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt">
              <option value="">Select a channel…</option>
              {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button data-testid="sticky-toggle-embed" onClick={() => set("use_embed", !form.use_embed)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${form.use_embed ? "bg-volt/10 border-volt/50 text-volt" : "border-zinc-800 text-zinc-400"}`}>
              {form.use_embed ? "Rich embed" : "Plain text"}
            </button>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              Re-post delay
              <input data-testid="sticky-delay" type="number" min="2" max="60" value={form.delay_seconds}
                onChange={(e) => set("delay_seconds", parseInt(e.target.value) || 5)}
                className="w-16 bg-black border border-zinc-800 rounded-md px-2 py-1 text-sm text-zinc-200" /> s
            </div>
          </div>

          {form.use_embed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="sticky-embed-title" value={form.embed_title} onChange={(e) => set("embed_title", e.target.value)} placeholder="Embed title" className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
              <div className="flex items-center gap-2">
                <input type="color" value={form.embed_color} onChange={(e) => set("embed_color", e.target.value)} className="w-10 h-9 bg-black border border-zinc-800 rounded-md" />
                <input value={form.embed_color} onChange={(e) => set("embed_color", e.target.value)} className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
              </div>
              <input data-testid="sticky-embed-footer" value={form.embed_footer} onChange={(e) => set("embed_footer", e.target.value)} placeholder="Footer text" className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
              <div className="relative">
                <ImageIcon className="w-4 h-4 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input data-testid="sticky-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="Image URL (optional)" className="w-full bg-black border border-zinc-800 rounded-md pl-8 pr-3 py-2 text-sm outline-none focus:border-volt" />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-zinc-500">{form.use_embed ? "Embed description" : "Message content"}</label>
              <div className="flex gap-1">
                {VARS.map((v) => <button key={v} onClick={() => insertVar(v)} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-volt hover:bg-zinc-700">{v}</button>)}
              </div>
            </div>
            <textarea data-testid="sticky-content" value={form.content} onChange={(e) => set("content", e.target.value)} rows={4}
              placeholder="What should stay pinned at the bottom? Supports {server}, {count}, {channel}" className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
          </div>

          <div className="flex items-center gap-2">
            <button data-testid="sticky-save" disabled={busy} onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"><Save className="w-4 h-4" /> {editingId ? "Save changes" : "Create sticky"}</button>
          </div>

          {/* Templates */}
          <div className="border-t border-zinc-800 pt-4">
            <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2"><Bookmark className="w-4 h-4 text-zinc-400" /> Templates</h4>
            <div className="flex gap-2 mb-2">
              <input data-testid="tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Save current as template…" className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-1.5 text-sm outline-none focus:border-volt" />
              <button data-testid="tpl-save" onClick={saveTemplate} className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5"><BookmarkPlus className="w-4 h-4" /> Save</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <span key={t.id} className="group flex items-center gap-1 bg-black/40 border border-zinc-800 rounded-full pl-3 pr-1.5 py-1 text-xs">
                  <button data-testid="tpl-apply" onClick={() => applyTemplate(t)} className="text-zinc-200 hover:text-volt">{t.name}</button>
                  <button onClick={() => deleteTemplate(t.id)} className="text-zinc-600 hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {templates.length === 0 && <span className="text-xs text-zinc-600">No templates yet — save one to reuse across channels.</span>}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-2 space-y-3">
          <h3 className="font-head font-semibold text-sm text-zinc-400">Active stickies {items && `(${items.length})`}</h3>
          {items === null && <p className="text-zinc-600 text-sm">Loading…</p>}
          {items && items.length === 0 && <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center text-zinc-600 text-sm" data-testid="sticky-empty">No sticky messages yet. Create one to keep rules or links always visible.</div>}
          {items && items.map((s) => (
            <div key={s.id} data-testid="sticky-row" className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-200 flex items-center gap-1"><Hash className="w-3.5 h-3.5 text-zinc-500" />{s.channel_name || s.channel}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.enabled ? "bg-volt/15 text-volt" : "bg-zinc-800 text-zinc-500"}`}>{s.enabled ? "Active" : "Paused"}</span>
                    {s.use_embed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">embed</span>}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{s.use_embed ? (s.embed_title || s.content) : s.content}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button data-testid="sticky-edit" onClick={() => edit(s)} className="text-xs text-zinc-300 hover:text-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                <button data-testid="sticky-power" onClick={() => toggle(s)} className="text-xs text-zinc-300 hover:text-white flex items-center gap-1"><Power className="w-3 h-3" /> {s.enabled ? "Pause" : "Resume"}</button>
                <button data-testid="sticky-delete" onClick={() => remove(s.id)} className="text-xs text-red-400/80 hover:text-red-400 flex items-center gap-1 ml-auto"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
